import { ChangeDetectorRef, Directive, inject, Input, SimpleChanges } from '@angular/core';
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { DfEventService } from '@df/core/service/df-event';
import { Subscription } from 'rxjs';
import { NDfModuleLoqate } from '../df-module-loqate.config';
import { LoqateEmailValidatorService } from './loqate-email-validator.service';

declare module '@df/core/service/df-event' {
    interface IEventType {
        [NDfModuleLoqate.Events.EmailValidation]: DfEventConfig<
            NDfModuleLoqate.Events.EmailValidation,
            {
                value: string | undefined | null;
            },
            void
        >;
    }
}

/**
 * A directive that adds the `loqate` validator to controls marked with the
 * `email` attribute, via the `NG_ASYNC_VALIDATORS` provider.
 *
 * @example
 * ```html
 * <input type="text" loqateEmail [(ngModel)]="email" />
 * ```
 */
@Directive({
    selector: '[loqateEmail][ngModel],[loqateEmail][formControl],[loqateEmail][formControlName]',
    standalone: true,
    providers: [
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: LoqateEmailValidateDirective,
            multi: true
        }
    ]
})
export class LoqateEmailValidateDirective implements AsyncValidator {
    @Input()
    loqateEmail = true;

    protected subscription?: Subscription;

    private _onChange!: () => void;

    /**
     * A flag that tracks whether this validator is enabled.
     */
    private _enabled?: boolean;

    protected loqateEmailValidatorService = inject(LoqateEmailValidatorService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected dfEventService = inject(DfEventService);

    ngOnChanges(changes: SimpleChanges) {
        if (changes['loqateEmail']) {
            const input = changes['loqateEmail'].currentValue;
            this._enabled = this.enabled(input);

            this.subscription?.unsubscribe();

            if (this._onChange) {
                this._onChange();
            }
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    validate(control: AbstractControl): Promise<ValidationErrors | null> {
        const promise = new Promise<ValidationErrors | null>(resolve => {
            if (!this._enabled) {
                resolve(null);
            } else {
                const v = this.loqateEmailValidatorService.validate(control);

                this.subscription = v.subscribe(val => {
                    resolve(val);
                    this.changeDetectorRef.detectChanges();
                });
            }
        });

        promise.then(() => {
            this.dfEventService.dispatch({
                id: NDfModuleLoqate.Events.EmailValidation,
                data: {
                    value: control.value
                }
            });
        });

        return promise;
    }

    /**
     * Registers a callback function to call when the validator inputs change.
     */
    registerOnValidatorChange(fn: () => void): void {
        this._onChange = fn;
    }

    enabled(input: boolean | string | null): boolean {
        if (typeof input === 'boolean') {
            return input;
        }

        if (typeof input === 'string') {
            return input !== 'false';
        }

        return true;
    }
}
